// Client-side image compression before upload.
//
// Large photos (phones shoot 5–12 MB) get rejected by the reverse proxy's body
// limit (413) before they ever reach the backend's sharp optimizer. We downscale
// + re-encode in the browser first so the upload lands under that limit while
// staying visually sharp. The backend still runs its own sharp pass (→ webp) on
// what we send.
//
// The byte ceiling is the point: capping the longest side alone is not enough —
// a detailed 2000px JPEG can still weigh 2 MB, and a screenshot re-encoded as
// PNG barely shrinks at all. So dimension and quality step down until the result
// fits BUDGET_BYTES (2026-07-30: a cover upload on prod 413'd for exactly this).
//
// Only raster images are touched. PDFs / non-images pass through unchanged.

const DIMENSIONS = [1600, 1280, 1024]; // longest side, px — tried in order
const QUALITIES = [0.85, 0.75, 0.62];
const BUDGET_BYTES = 700 * 1024; // comfortably below a 1 MB proxy default
const SKIP_BELOW_BYTES = 300 * 1024; // already small — leave it alone

const canCompress = (file) =>
  file &&
  file.type &&
  file.type.startsWith("image/") &&
  file.type !== "image/gif" && // don't flatten animated gifs
  file.type !== "image/svg+xml"; // vector — nothing to downscale

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// WebP keeps transparency AND compresses far better than PNG, so it is the
// preferred output whenever the browser can encode it.
let webpSupport = null;
const supportsWebp = () => {
  if (webpSupport !== null) return webpSupport;
  try {
    const c = document.createElement("canvas");
    c.width = 1;
    c.height = 1;
    webpSupport = c.toDataURL("image/webp").startsWith("data:image/webp");
  } catch {
    webpSupport = false;
  }
  return webpSupport;
};

const drawScaled = (img, maxDimension) => {
  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
};

/**
 * Compress an image File. Returns a new File (smaller) or the original on any
 * failure / when compression isn't worth it. Never throws.
 */
export const compressImage = async (file) => {
  try {
    if (!canCompress(file)) return file;
    if (file.size <= SKIP_BELOW_BYTES) return file;

    const img = await loadImage(await readAsDataUrl(file));

    const isPng = file.type === "image/png";
    // Without WebP support a PNG has to stay PNG or it loses transparency; its
    // quality argument is ignored, so only the dimension loop does any work.
    const outType = supportsWebp() ? "image/webp" : isPng ? "image/png" : "image/jpeg";
    const ext = outType === "image/webp" ? "webp" : outType === "image/png" ? "png" : "jpg";

    let best = null;
    for (const dimension of DIMENSIONS) {
      const canvas = drawScaled(img, dimension);
      for (const quality of QUALITIES) {
        // eslint-disable-next-line no-await-in-loop
        const blob = await canvasToBlob(canvas, outType, quality);
        if (blob && (!best || blob.size < best.size)) best = blob;
        if (best && best.size <= BUDGET_BYTES) break;
        if (outType === "image/png") break; // quality is a no-op for PNG
      }
      if (best && best.size <= BUDGET_BYTES) break;
    }

    // Bail if compression didn't actually help (e.g. already-optimized asset).
    if (!best || best.size >= file.size) return file;

    const baseName = file.name ? file.name.replace(/\.[^.]+$/, "") : "image";
    return new File([best], `${baseName}.${ext}`, {
      type: outType,
      lastModified: Date.now(),
    });
  } catch {
    return file; // any decode/encode failure → upload the original
  }
};

export default compressImage;
